import { useState } from 'react';
import { C, S } from '../constants';
import Img from '../components/Img';
import ProdLink from '../components/ProdLink';

const fmtUAH = (v) => v > 0 ? Math.round(v).toLocaleString("uk-UA") + " ₴" : null;

const GROUPS = [
  { key: "oos_with_demand",   icon: "📦", label: "ПОПОВНИ ЗАПАС",       color: C.pink,    sub: "є попит, нема залишку" },
  { key: "sales_dropout",     icon: "📉", label: "ЗУПИНИЛИСЬ ПРОДАЖІ",  color: C.pink,    sub: "продавались регулярно, зупинились" },
  { key: "bad_reviews",       icon: "⭐", label: "НЕГАТИВНІ ВІДГУКИ",   color: C.pink,    sub: "з'явились 1-2 зіркові відгуки" },
  { key: "low_ff_stock",      icon: "⚠️", label: "ЗАКІНЧУЄТЬСЯ НА FF",  color: "#fb923c", sub: "мало залишку на складі Rozetka" },
  { key: "position_drop",     icon: "↓",  label: "ВПАЛИ В ПОШУКУ",      color: "#fb923c", sub: "позиція погіршилась > 5 місць" },
  { key: "promo_opportunity", icon: "🏷", label: "ВСТАНОВИ ПРОМО",      color: "#fb923c", sub: "Rozetka просуває без промо-ціни" },
  { key: "ff_bypass",         icon: "⚡", label: "FF БАЙПАС",           color: "#fb923c", sub: "є на складі Rozetka, йде як FBS" },
  { key: "position_gain",     icon: "↑",  label: "ПІДНЯЛИСЬ — ШАНС",   color: C.green,   sub: "позиція покращилась > 5 місць" },
];

const REASON_BADGE = {
  oos:           { label: "OOS",     color: C.pink },
  price_changed: { label: "ціна",    color: "#fb923c" },
  position_drop: { label: "позиція", color: "#fb923c" },
  bad_review:    { label: "відгук",  color: C.pink },
  unknown:       { label: "?",       color: C.text4 },
};

function buildItems(key, data) {
  const { urgent = {}, watch = {}, good = {} } = data;
  const { sales_dropout = [], oos_with_demand = [] } = urgent;
  const { position_drops = [], ff_bypass = [], promo_opportunity = [] } = watch;
  const { position_gains = [] } = good;

  switch (key) {
    case "oos_with_demand":
      return oos_with_demand.map(p => {
        const impact = p.catalog_price > 0 ? Math.round((p.avg_daily_est || 0) * p.catalog_price) : null;
        return { p, context: `${p.recent_orders} зам. за 7 днів поки залишок = 0`, impact: impact ? `~${fmtUAH(impact)}/день` : `${p.recent_orders} зам/тиж`, impactColor: C.pink };
      });
    case "sales_dropout":
      return sales_dropout.map(p => {
        const impact = p.catalog_price > 0 ? Math.round((p.avg_daily || 0) * p.catalog_price) : null;
        const ctxMap = {
          oos:           "немає залишку — покупці не можуть замовити",
          price_changed: "ціна змінилась — можливо конкурент дешевше",
          position_drop: "впала позиція в категорії",
          bad_review:    "з'явився поганий відгук",
          unknown:       `зазвичай ${(p.avg_daily || 0) >= 1 ? Math.round(p.avg_daily) + " зам/день" : Math.round((p.avg_daily || 0) * 7) + " зам/тиж"}, зараз 0`,
        };
        return { p, reason: p.reason || "unknown", context: ctxMap[p.reason] || ctxMap.unknown, impact: impact ? `~${fmtUAH(impact)}/день` : null, impactColor: C.pink };
      });
    case "position_drop":
      return position_drops.map(p => ({ p, context: `#${p.position_prev} → #${p.position} (−${p.drop} місць)`, impact: `#${p.position_prev} → #${p.position}`, impactColor: "#fb923c" }));
    case "promo_opportunity":
      return promo_opportunity.map(p => ({ p, context: "Rozetka активно просуває, але немає промо-ціни", impact: fmtUAH(p.promo_required_price) ? `рек. ${fmtUAH(p.promo_required_price)}` : null, impactColor: "#fb923c" }));
    case "ff_bypass":
      return ff_bypass.map(p => ({ p, context: `${p.ff_stock} шт. на складі Rozetka, ${p.fbs_orders} зам. як FBS за 14д`, impact: `FF: ${p.ff_stock} шт.`, impactColor: "#fb923c" }));
    case "position_gain":
      return position_gains.map(p => ({ p, context: `#${p.position_prev} → #${p.position} (+${p.gain} місць)`, impact: `#${p.position_prev} → #${p.position}`, impactColor: C.green }));
    case "bad_reviews": {
      const reviews = data.watch?.bad_reviews ?? [];
      return reviews.map(p => {
        let when = null;
        if (p.neg_detected_at) {
          const days = Math.round((Date.now() - new Date(p.neg_detected_at)) / 86400000);
          when = days === 0 ? "сьогодні" : days === 1 ? "вчора" : `${days} дн. тому`;
        }
        const stars = "★".repeat(p.top_rating || 1) + "☆".repeat(5 - (p.top_rating || 1));
        const context = [
          stars,
          p.agg_rating ? `agg ${p.agg_rating.toFixed(1)}` : null,
          when ? `виявлено ${when}` : null,
        ].filter(Boolean).join(" · ");
        return { p, context, impact: null, impactColor: C.pink };
      });
    }
    case "low_ff_stock": {
      const items = data.watch?.low_ff_stock ?? [];
      return items.map(p => ({
        p,
        context: `${p.ff_stock} шт. на складі Rozetka · ${p.velocity > 0 ? (p.velocity).toFixed(1) + " зам/день" : ""}`,
        impact: p.days_left ? `~${p.days_left} дн.` : `${p.ff_stock} шт.`,
        impactColor: p.days_left && p.days_left <= 3 ? C.pink : "#fb923c",
      }));
    }
    default:
      return [];
  }
}

export default function InsightsPage({ insightsData, insightsLoading, onRefresh }) {
  const [selected, setSelected] = useState(null);

  if (insightsLoading) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: 80, color: C.text3 }}>
        Завантаження...
      </div>
    );
  }
  if (!insightsData) return null;

  const generatedAt = insightsData.meta?.generated_at
    ? new Date(insightsData.meta.generated_at).toLocaleTimeString("uk-UA", { hour: "2-digit", minute: "2-digit" })
    : null;

  const groups = GROUPS
    .map(g => ({ ...g, items: buildItems(g.key, insightsData) }))
    .filter(g => g.items.length > 0);

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 20, fontWeight: 800 }}>Інсайти</div>
          {generatedAt && <div style={{ fontSize: 11, color: C.text4, marginTop: 2 }}>оновлено о {generatedAt}</div>}
        </div>
        <button onClick={onRefresh} style={{ ...S.btn(false, C.blue), fontSize: 12, padding: "6px 14px" }}>
          Оновити
        </button>
      </div>

      {groups.length === 0 ? (
        <div style={{ ...S.card, textAlign: "center", padding: 48 }}>
          <div style={{ fontSize: 28, marginBottom: 10, color: C.green }}>✓</div>
          <div style={{ fontSize: 15, fontWeight: 700, color: C.text1 }}>Все добре</div>
          <div style={{ fontSize: 12, color: C.text4, marginTop: 6 }}>Жодних аномалій не виявлено</div>
        </div>
      ) : (
        <div style={{ display: "flex", gap: 12, height: "calc(100vh - 180px)" }}>
          {groups.map(g => (
            <div
              key={g.key}
              style={{
                flex: 1,
                background: `linear-gradient(145deg, ${g.color}28 0%, ${g.color}06 100%)`,
                border: `1px solid ${g.color}40`,
                borderTop: `3px solid ${g.color}`,
                borderRadius: 14,
                padding: "24px 20px 18px",
                display: "flex",
                flexDirection: "column",
                boxSizing: "border-box",
                minWidth: 0,
              }}
            >
              <div style={{ fontSize: 30 }}>{g.icon}</div>
              <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "1px", fontWeight: 700, color: C.text3, marginTop: 14 }}>
                {g.label}
              </div>
              <div style={{ fontSize: 52, fontWeight: 800, color: C.text1, lineHeight: 1, margin: "10px 0 8px" }}>
                {g.items.length}
              </div>
              <div style={{ fontSize: 12, color: C.text4, flex: 1, lineHeight: 1.4 }}>{g.sub}</div>
              <div style={{ marginTop: 14, borderTop: `1px solid ${g.color}30`, paddingTop: 12 }}>
                <button
                  onClick={() => setSelected(g)}
                  style={{ background: "none", border: "none", padding: 0, cursor: "pointer", fontSize: 13, fontWeight: 600, color: g.color }}
                >
                  Переглянути товари →
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {selected && (
        <div
          onClick={() => setSelected(null)}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: C.bg1,
              border: `1px solid ${C.border}`,
              borderRadius: 14,
              width: "min(620px, 95vw)",
              maxHeight: "80vh",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            <div style={{ padding: "16px 20px", borderBottom: `1px solid ${C.border}`, display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
              <span style={{ fontSize: 20 }}>{selected.icon}</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: C.text1 }}>{selected.label}</div>
                <div style={{ fontSize: 11, color: C.text4 }}>{selected.items.length} товарів · {selected.sub}</div>
              </div>
              <button
                onClick={() => setSelected(null)}
                style={{ background: "none", border: "none", cursor: "pointer", fontSize: 20, color: C.text3, lineHeight: 1, padding: "0 4px" }}
              >
                ×
              </button>
            </div>
            <div style={{ overflowY: "auto", flex: 1 }}>
              {selected.items.map(({ p, context, impact, impactColor, reason }, i) => (
                <div
                  key={p.rozetka_id ?? i}
                  style={{
                    display: "flex", alignItems: "center", gap: 10,
                    padding: "10px 20px",
                    borderBottom: i < selected.items.length - 1 ? `1px solid ${C.border}` : "none",
                  }}
                >
                  <Img src={p.image_url} size={32} style={{ flexShrink: 0, borderRadius: 4 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                      <ProdLink name={p.name} article={p.article} productId={p.rozetka_id} style={{ fontSize: 12, fontWeight: 600, color: C.text1 }} />
                      {reason && REASON_BADGE[reason] && (
                        <span style={{
                          fontSize: 10, fontWeight: 700, padding: "1px 5px", borderRadius: 4,
                          background: REASON_BADGE[reason].color + "20", color: REASON_BADGE[reason].color,
                          border: `1px solid ${REASON_BADGE[reason].color}40`,
                        }}>
                          {REASON_BADGE[reason].label}
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: 11, color: C.text4, marginTop: 2 }}>{context}</div>
                  </div>
                  {impact && (
                    <div style={{
                      fontSize: 12, fontWeight: 700, color: impactColor,
                      background: impactColor + "18", border: `1px solid ${impactColor}30`,
                      borderRadius: 6, padding: "3px 8px", whiteSpace: "nowrap", flexShrink: 0,
                    }}>
                      {impact}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
